import { extractPriceAndDiscountClaims } from "./figures";
import type { WriteTier } from "./types";
import type { WriteCategory } from "./writeTiers";

/**
 * V11, Training and Auto-Learn Master Spec B9: "Teach-your-AI box: owner types
 * anything, anytime, becomes a directive or testable hypothesis". The vault
 * synthesis rates it OFFLINE and record-only, so this module only READS a
 * note. It says which write tier the note would touch if it were ever applied
 * (CLAUDE.md section 3, `writeTiers.ts`), and nothing here, or in the store
 * that calls it, applies one to a prompt, the KB, a playbook, a threshold,
 * memory or an artifact.
 *
 * A READING, NOT A DECISION. B9: "never silently overrides, never silently
 * obeys into losses". The reading exists so the owner is told, at the moment
 * of typing, that a note about a price is not something a note can change, and
 * so the later approval path (Kabir's, not built) starts from a tag rather
 * than from nothing.
 *
 * EXPLICIT CUES ONLY. A tier is read from the words in the table below and
 * from the price and discount figures the shared reader finds (F-16), and
 * from nothing else: no model, no guess. A note with no cue is `unsure`,
 * which claims nothing (section 0, when unsure, don't).
 *
 * THE STRICTEST CUE WINS. T3 beats T2 beats unsure. A note that is about the
 * voice AND about price reads T3, because applying it would touch price. A
 * mention is enough: "don't discount rings" touches the discount cap as much
 * as "discount rings" does, and over-reading toward T3 costs a sentence of
 * explanation where under-reading it costs the wall in section 3.
 *
 * NEVER T1. T1 is "auto-shippable after floors, eval regression and a
 * challenger shadow" (patterns, few-shots, cadence, retrieval weights). An
 * owner's note ships nothing on its own, so no cue here reads T1 and the type
 * cannot express it: a note about cadence is `unsure`, not T1.
 *
 * HELD TO THE REGISTRY. Each row names the registry category it speaks for
 * where one exists, and `ownerNoteTier.test.ts` holds three things to
 * `WRITE_TIERS`: a row's tier is the registry's tier for its category, every
 * T3 category in the registry is named by some row (a category added later
 * cannot go unread), and the only T2 rows are KB facts and voice. Three rows
 * name no category on purpose: a bare "cap" does not say whether it is the
 * price cap or the discount cap, "refund" is a policy word the registry does
 * not tier (the item names it T3, and T3 is the conservative reading), and a
 * price figure is not a cap. Their tier is the item's, stated on the row.
 */

/** What the owner calls the note. Their word, never the machine's: `unsure` is the default. */
export const OWNER_NOTE_KINDS = ["directive", "hypothesis", "unsure"] as const;
export type OwnerNoteKind = (typeof OWNER_NOTE_KINDS)[number];
export const DEFAULT_OWNER_NOTE_KIND: OwnerNoteKind = "unsure";

export const isOwnerNoteKind = (word: string): word is OwnerNoteKind =>
  (OWNER_NOTE_KINDS as readonly string[]).includes(word);

/**
 * Where a note stands. `open` is a note nothing has read yet. W5 OWNERIN adds
 * the idea pool's states, each reachable by code in `db/teachVerbs.ts`:
 * `in_pool` (a hypothesis with no countable predicate yet), `mapped` (a
 * hypothesis with a predicate in the closed catalog), `tested` (the learner has
 * registered it), `confirmed` and `declined` (the owner's reply), `superseded`
 * (a later note replaced it), `t3_held` (it touches a T3 item, never pooled)
 * and `awaiting_item_130` (it touches a T2 item, or is a directive, and waits
 * for the approval path that is not built).
 */
export const OWNER_NOTE_STATUSES = [
  "open",
  "in_pool",
  "mapped",
  "tested",
  "confirmed",
  "declined",
  "superseded",
  "t3_held",
  "awaiting_item_130",
] as const;
export type OwnerNoteStatus = (typeof OWNER_NOTE_STATUSES)[number];

/** T2, T3, or no cue. Never T1: see the header. */
export type OwnerNoteTier = Extract<WriteTier, "T2" | "T3"> | "unsure";

/** Printed under every T3 note, at add and in the list. Word for word the item's. */
export const T3_NOTE_LINE = "T3: only Kabir changes this in code, never from a note";

export interface OwnerNoteCue {
  /** The word printed for this row. Fixed here, never a slice of the note. */
  readonly cue: string;
  readonly tier: Extract<WriteTier, "T2" | "T3">;
  /** The registry category this row speaks for, or null (see the header). */
  readonly category: WriteCategory | null;
  /** Reads the note after NFKC folding, so fullwidth letters do not hide a cue. */
  readonly fires: (text: string) => boolean;
}

/** A whole word, not a part of a longer one, in any script. */
const wholeWord = (alternatives: string): ((text: string) => boolean) => {
  const re = new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternatives})(?![\\p{L}\\p{N}])`, "iu");
  return (text) => re.test(text);
};

/**
 * The price words: what a shop calls the number a customer pays. "Price" is
 * one; cost, charge, fee, dollars, bucks, quote, cheap and expensive are
 * others, and a note that says "charge 40 dollars for resizing" changes a
 * price whether or not it says "price". They sit on the price row, not the KB
 * row, so the strictest-cue rule reads T3 for them even beside a KB word
 * (resizing, engraving, shipping). "In charge" is a person, not a price. "Free"
 * is a cue only in the phrases that mean no charge ("for free", "free of
 * charge", free shipping and the like), never bare: "feel free" and "a free
 * hand" are not prices.
 *
 * DEPOSIT, RATE AND A BARE "FREE" STAY OFF THE LIST. RATIFIED by Kabir
 * 2026-10-02 (decision list item 156, typed reply "Yes I want to continue the
 * production" to the accept-the-recs question). A deposit holds an order and a
 * rate measures anything ("reply rate"), so a note that says either reads
 * `unsure` unless another cue fires, like any note with no cue.
 */
const PRICE_WORDS = [
  "(?:over|under|re|mis)?pric(?:e|es|ed|ing|ey)|pricier|priciest",
  "cost(?:s|ed|ing)?",
  "(?<!\\bin\\s)charg(?:e|es|ed|ing)",
  "fees?",
  "dollars?",
  "bucks?",
  "quot(?:e|es|ed|ing)",
  "cheap(?:er|est|ly|en|ened)?",
  "expensive",
  "for free",
  "free (?:of charge|shipping|delivery|returns?|resizing|engraving|repairs?|cleaning)",
].join("|");

/** The discount words, including a reduction said in words or as "N% less" ("N% off" is the figure reader's). */
const DISCOUNT_WORDS = [
  "discount(?:s|ed|ing)?|markdowns?|coupons?|percent off|promo codes?",
  "percent less|per cent less",
  "\\d+(?:\\.\\d+)?\\s?% less",
].join("|");

/**
 * The cue table, in the order a reading lists what fired. T3 rows first: the
 * four words the item names (price, discount, cap, refund), then the T3 names
 * CLAUDE.md section 3 lists, so a note about a guardrail or a complaint is not
 * read as "no cue". Then the two T2 rows the item names (KB facts, voice).
 */
export const OWNER_NOTE_CUES: readonly OwnerNoteCue[] = Object.freeze([
  // T3: the item's four words.
  { cue: "price", tier: "T3", category: "price-caps", fires: wholeWord(PRICE_WORDS) },
  { cue: "discount", tier: "T3", category: "discount-caps", fires: wholeWord(DISCOUNT_WORDS) },
  { cue: "cap", tier: "T3", category: null, fires: wholeWord("caps?|capped|capping") },
  { cue: "refund", tier: "T3", category: null, fires: wholeWord("refund(?:s|ed|ing)?") },
  // T3: a price or discount figure the shared reader finds (F-16), with or without a word.
  // DISC fix round 5: an owner note is the shop's words, read by every reader (the T3 direction).
  {
    cue: "price-or-discount-figure",
    tier: "T3",
    category: null,
    fires: (text) => extractPriceAndDiscountClaims(text, { author: "shop" }).length > 0,
  },
  // T3: CLAUDE.md section 3, the rest of the list.
  { cue: "guardrails", tier: "T3", category: "guardrails", fires: wholeWord("guardrails?") },
  { cue: "escalation", tier: "T3", category: "escalation-triggers", fires: wholeWord("escalat(?:e|es|ed|ing|ion|ions)") },
  { cue: "sla", tier: "T3", category: "sla-timers", fires: wholeWord("slas?") },
  { cue: "compliance", tier: "T3", category: "compliance-rules", fires: wholeWord("complian(?:ce|t)|ftc|disclosures?|jewelry guides|review solicitation") },
  { cue: "wealth", tier: "T3", category: "wealth-differentiated-pricing", fires: wholeWord("wealth|wealthy") },
  {
    cue: "messaging-window",
    tier: "T3",
    category: "messaging-window-state-machine",
    fires: wholeWord("24[\\s-]?(?:h|hr|hrs|hour|hours)|messaging window|window state"),
  },
  { cue: "learning-charter", tier: "T3", category: "learning-charter", fires: wholeWord("learning charter|charter") },
  { cue: "validation-bar", tier: "T3", category: "auto-learn-validation-bars", fires: wholeWord("validation (?:bars?|floors?)") },
  { cue: "reward-weights", tier: "T3", category: "adaptive-reward-weights", fires: wholeWord("reward weights?|objective function") },
  { cue: "class-d", tier: "T3", category: "class-d-scenario-rows", fires: wholeWord("complaints?|fraud(?:ulent)?|chargebacks?|class d") },
  // T2: the two the item names. Deliberately short: a T2 reading says "an
  // approval could apply this", which is a claim, so only explicit words make it.
  {
    cue: "voice",
    tier: "T2",
    category: "voice-profile",
    fires: wholeWord("voice|tone|greetings?|sign[\\s-]?offs?|emojis?|exclamations?|sounds? (?:like|more|less)"),
  },
  {
    cue: "kb",
    tier: "T2",
    category: "kb-facts",
    fires: wholeWord(
      "kb|knowledge base|opening hours|store hours|hours|closed|turnaround|lead time|resiz\\w+|warrant(?:y|ies)|engrav\\w+|shipping|financing|appointments?|trade[\\s-]?ins?",
    ),
  },
] satisfies readonly OwnerNoteCue[]);

export interface OwnerNoteReading {
  readonly tier: OwnerNoteTier;
  /** Every row that fired, by its fixed word, in table order. */
  readonly cues: readonly string[];
  /** The registry categories those rows speak for, once each, in table order. */
  readonly categories: readonly WriteCategory[];
}

/**
 * Which write tier a note would touch if it were ever applied. Pure: the same
 * text always reads the same, nothing is thrown, an empty note is `unsure`.
 * It reads; it never applies, queues or proposes anything.
 */
export function readOwnerNoteTier(text: string): OwnerNoteReading {
  const folded = text.normalize("NFKC");
  const fired = OWNER_NOTE_CUES.filter((row) => row.fires(folded));
  const categories: WriteCategory[] = [];
  for (const row of fired) {
    if (row.category !== null && !categories.includes(row.category)) categories.push(row.category);
  }
  const tier: OwnerNoteTier = fired.some((r) => r.tier === "T3") ? "T3" : fired.some((r) => r.tier === "T2") ? "T2" : "unsure";
  return { tier, cues: fired.map((r) => r.cue), categories };
}
